// src/auth/passwordResetApi.js
//
// The three PUBLIC recovery endpoints. Spec: <BACKEND_FOLDER>/docs/frontend/password-reset-flow.md
// and <BACKEND_FOLDER>/docs/frontend/set-password-flow.md.
//
// ⛔ Deliberately bare `fetch`, NOT apiFetch. These routes are unauthenticated by
// design (the caller cannot log in — that is the problem being solved). apiFetch
// would attach a stale Authorization + X-Tenant-Id and, on a 401, wipe auth
// storage and redirect. AuthContext.login() sets the precedent followed here.
//
// ⚠️ The error envelope is FLAT: q/src/api/errors.py spreads **exc.detail into
// the body, so `reason` and `retry_after_seconds` are TOP-LEVEL.
// `body.detail.error_code` is undefined. buildApiError hoists top-level extras
// onto the Error, so callers read err.code / err.reason / err.retry_after_seconds.
// ⭐ v221: /auth/set-password carries the terms acceptance (legal/termsModel.termsBody) and, only when /set-password/info asked, marketing_opt_in.
import { API_V1, getSessionHeaders } from '../services/api/config.js';
import { buildApiError } from '../context/parseApiError.js';
import { termsBody } from '../legal/termsModel.js';

async function postPublic(path, body) {
  let res;
  try {
    res = await fetch(`${API_V1}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      body: JSON.stringify(body),
    });
  } catch {
    // Transport failure — give it a code so screens can branch without
    // string-matching the message.
    const err = new Error('NETWORK');
    err.code = 'NETWORK';
    throw err;
  }
  if (!res.ok) {
    const parsed = await res.json().catch(() => ({}));
    throw buildApiError(parsed, res.status);
  }
  return res;
}

/**
 * Ask for a recovery link.
 *
 * ⛔ ALWAYS resolves on 202 — unknown address, deactivated account, superadmin
 * and already-active user are all 202, and the backend decides which of the two
 * mails (password-reset or set-password) goes out. The caller MUST NOT render
 * this as confirmation that mail was sent, and MUST NOT branch on it.
 */
export async function requestPasswordReset(email) {
  await postPublic('/auth/password-reset', { email });
}

/** Spend a password-reset link. 204 on success. */
export async function confirmPasswordReset({ token, password }) {
  await postPublic('/auth/password-reset/confirm', { token, password });
}

/**
 * Spend an onboarding (set-password) link. 204 on success; also activates the account.
 * ⭐ v221: `terms` = the normalized terms the page SHOWED (legal/termsModel) → terms_accepted + the two versions,
 * null included. `marketingOptIn` is sent ONLY when it is a boolean, i.e. when /set-password/info asked for it.
 * A 409 TERMS_VERSION_CHANGED leaves the link valid (the BE checks before spending it).
 */
export async function setPassword({ token, password, terms, marketingOptIn }) {
  const body = { token, password, ...termsBody(terms) };
  if (typeof marketingOptIn === 'boolean') body.marketing_opt_in = marketingOptIn;
  await postPublic('/auth/set-password', body);
}

/**
 * What the set-password page shows for this link: {askMarketingOptIn}. Never spends the link.
 * ⛔ The token goes in the BODY, never in a URL (the request line is logged). A dead link answers the same
 * 400 SET_PASSWORD_TOKEN_INVALID (+ top-level `reason`) as set-password.
 */
export async function fetchSetPasswordInfo(token) {
  const res = await postPublic('/auth/set-password/info', { token });
  const json = await res.json().catch(() => ({}));
  return { askMarketingOptIn: json?.ask_marketing_opt_in === true };
}

// ⛔ NO resendSetPassword export. POST /auth/set-password/resend still exists on
// the backend but only ever serves the never-onboarded row; a "Forgot password?"
// button wired to it silently does nothing for the users who most need it
// (<BACKEND_FOLDER>/docs/frontend/password-reset-flow.md, "One button, two mails"). The unified
// /auth/password-reset above covers that row too.
